import React, { createContext, useContext, useState, ReactNode } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Typography } from '@mui/material';

export type AccountingProvider = 'FORTNOX' | 'VISMA';

export const ACCOUNTING_PROVIDER_LABEL: Record<AccountingProvider, string> = {
    FORTNOX: 'Fortnox',
    VISMA: 'Visma',
};

/**
 * One refusal from the lab's accounting system, as the backend attaches it in
 * `otherInfo.rejections` of an accounting `USER_FRONTEND_FEEDBACK` error.
 */
export type AccountingRejection = {
    provider: AccountingProvider;
    /** The accounting system's own message, verbatim. */
    message: string;
    code?: string;
    /** Clinic (or "Clinic - Dentist") the invoice was for. */
    customerName?: string;
    /** Set when the invoice already exists in the accounting system and only a later step failed. */
    providerInvoiceNumber?: string;
};

/** The `otherInfo` a `USER_FRONTEND_FEEDBACK` error carries; also what `showUserFrontendFeedback` takes. */
export type UserFrontendFeedback = {
    translationTitleKey: string;
    translationMessageKey: string;
    /** i18next interpolation values for the title and message. */
    translationParams?: Record<string, string | number>;
    rejections?: AccountingRejection[];
};

type DenthubErrorBoundaryContextType = {
    showBoundary: (error: any) => void;
    /** Opens the feedback dialog without a GraphQL error, e.g. after a mutation that only partly succeeded. */
    showUserFrontendFeedback: (feedback: UserFrontendFeedback) => void;
    userFrontendFeedbackError: any;
    setUserFrontendFeedbackError: (err: any) => void;
};

export const isUserFrontendFeedbackError = (error: any): boolean =>
    error?.graphQLErrors?.[0]?.extensions?.exception?.type === 'USER_FRONTEND_FEEDBACK';

const DenthubErrorBoundaryContext = createContext<DenthubErrorBoundaryContextType | undefined>(undefined);

export function DenthubErrorBoundaryProvider({ children }: { children: ReactNode }) {
    const { showBoundary: originalShowBoundary } = useErrorBoundary();
    const [userFrontendFeedbackError, setUserFrontendFeedbackError] = useState<any>(null);

    function showBoundary(error: any) {
        if (isUserFrontendFeedbackError(error)) {
            setUserFrontendFeedbackError(error);
            return;
        }
        originalShowBoundary(error);
    }

    function showUserFrontendFeedback(feedback: UserFrontendFeedback) {
        setUserFrontendFeedbackError({
            graphQLErrors: [{ extensions: { exception: { type: 'USER_FRONTEND_FEEDBACK', otherInfo: feedback } } }],
        });
    }

    return (
        <DenthubErrorBoundaryContext.Provider
            value={{ showBoundary, showUserFrontendFeedback, userFrontendFeedbackError, setUserFrontendFeedbackError }}
        >
            {children}
        </DenthubErrorBoundaryContext.Provider>
    );
}

export function useDenthubErrorBoundary() {
    const ctx = useContext(DenthubErrorBoundaryContext);
    if (!ctx) throw new Error('useDenthubErrorBoundary must be used within DenthubErrorBoundaryProvider');
    return ctx;
}

function AccountingRejectionList({ rejections }: { rejections: AccountingRejection[] }) {
    const { t: translate } = useTranslation(['errors']);
    return (
        <Box display="flex" flexDirection="column" gap={1.5} data-test="accounting-rejections">
            {rejections.map((rejection, index) => {
                const provider = ACCOUNTING_PROVIDER_LABEL[rejection.provider] ?? rejection.provider;
                return (
                    <Box key={index}>
                        <Typography variant="subtitle2">{rejection.customerName ?? provider}</Typography>
                        <Typography variant="body2">
                            {rejection.message}
                            {rejection.code ? ` (${rejection.code})` : ''}
                        </Typography>
                        {rejection.providerInvoiceNumber && (
                            <Typography variant="caption" color="warning.main">
                                {translate('errors:ACCOUNTING_REJECTED_AFTER_CREATE_HINT', {
                                    provider,
                                    invoiceNumber: rejection.providerInvoiceNumber,
                                })}
                            </Typography>
                        )}
                    </Box>
                );
            })}
        </Box>
    );
}

// Dialog component to be rendered at the top level (e.g. in AppContainer)
export function UserFrontendFeedbackDialog() {
    const { userFrontendFeedbackError, setUserFrontendFeedbackError } = useDenthubErrorBoundary();
    const { t: translate } = useTranslation(['errors', 'common']);
    if (!userFrontendFeedbackError) return null;
    const err = userFrontendFeedbackError?.graphQLErrors?.[0];
    const otherInfo: Partial<UserFrontendFeedback> = err?.extensions?.exception?.otherInfo ?? {};
    const params = otherInfo.translationParams ?? {};
    const close = () => setUserFrontendFeedbackError(null);
    return (
        <DHDialog
            open
            size="sm"
            fullWidth
            onClose={close}
            title={
                otherInfo.translationTitleKey
                    ? translate(`errors:${otherInfo.translationTitleKey}`, params)
                    : translate('errors:USER_FEEDBACK_DEFAULT_TITLE')
            }
            actions={
                <Button onClick={close} autoFocus data-test="user-feedback-dialog.close">
                    {translate('common:CLOSE')}
                </Button>
            }
        >
            <Box>
                <Alert severity="warning" sx={{ mb: 1 }}>
                    {otherInfo.translationMessageKey
                        ? translate(`errors:${otherInfo.translationMessageKey}`, params)
                        : translate('errors:USER_FEEDBACK_DEFAULT_ERROR')}
                </Alert>
                {otherInfo.rejections && otherInfo.rejections.length > 0 && (
                    <Box mt={2}>
                        <AccountingRejectionList rejections={otherInfo.rejections} />
                    </Box>
                )}
            </Box>
        </DHDialog>
    );
}
